import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router';
import orderBy from 'lodash/orderBy';
import debounce from 'lodash/debounce';
import InfiniteScroll from 'react-infinite-scroller';
import { useFlags } from 'launchdarkly-react-client-sdk';

// Utils
import PageUrlsUtil from 'Shared/utils/PageUrls.utils';

// Hooks
import { useDeepEqualSelector, useDeepCompareMemoize, useToggle, useFeature } from 'Shared/utils/hooks';
import useWindowDimensions from 'Wpds/hooks/useWindowDimensions';
import { useWorkspace } from 'Shared/utils/hooks/use-workspace';
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';

// Actions
import * as FeedbackRequestsActions from 'Shared/actions/FeedbackRequests.actions';
import {
  fetchFeedbacks,
  fetchFeedbackDrafts,
  destroyFeedback,
  fetchSingleFeedback,
} from 'Shared/actions/Feedbacks.actions';

// Selectors
import { getFeedbacksByWorkspaceUuid, getDraftFeedbackByWorkspaceUuid } from 'Shared/selectors/Feedbacks.selectors';
import { getWorkspaceTeammate } from 'Shared/selectors/workspace';

// Components
import Spinner from 'Shared/components/Spinner';
import InviteTeammateModal from 'Shared/components/InviteTeammateModal/InviteTeammateModal';
import { WpButton, WpButtonGroup } from 'Wpds/components/button';
import WpIcon from 'Wpds/components/icon/WpIcon';
import GiveKudosButton from 'Shared/components/GiveKudosButton/GiveKudosButton';
import CoachingIntroModal from 'Dashboard/components/CoachingIntroModal/CoachingIntroModal';
import FeedbackEmptyState from './EmptyState/FeedbackEmptyState';
import FeedbackItem from './FeedbackItem/FeedbackItem';
import DraftFeedback from './DraftFeedback/DraftFeedback';
import GiveFeedbackAndKudoButton from '../../../partials/GiveFeedbackAndKudoButton/GiveFeedbackAndKudoButton';

import './FeedbackAndKudosSection.scss';
import { Feature } from 'Shared/types/Organization.type';
import { FeedbackRecognitionRequestTypes, FeedbackRecognitionType } from 'Shared/constants/types';

type Props = {
  pageRef: { current: HTMLDivElement };
};

const FETCH_LIMIT = 5;

function FeedbackAndKudosSection({ pageRef }: Props) {
  const history = useHistory();
  const dispatch = useDispatch();
  const { width } = useWindowDimensions();
  const { newFeatureIntroModals: showNewFeatureModal } = useFlags();
  const { isActive: isKudosActive } = useFeature(Feature.kudos);

  // need a larger scope for mobile for when 3 buttons are on canvas (customize, request button, and give button)
  const isMobile = width < 1090;
  const [isInviteTeammateModalOpen, toggleIsInviteTeammateModalOpen] = useToggle(false);

  const { feedbackUuid: focusedFeedbackUuid } = useParams();
  const { isGroup, isPersonal, id: workspaceId, name: workspaceName } = useWorkspace();
  const { isManager } = useCurrentUser();

  const teammate = useDeepEqualSelector(getWorkspaceTeammate(workspaceId));
  const allowIntroModal = !isGroup && !isPersonal;

  const isNotInvited = () => {
    if (isGroup) return false;
    return Boolean(!teammate);
  };

  const feedbacks = useDeepEqualSelector(state =>
    getFeedbacksByWorkspaceUuid(workspaceId)(state)?.filter(({ isDraft }) => !isDraft)
  ) as FeedbackRecognitionType[];

  // Filter out the Kudos recognition if that feature is not active
  const feedBackDataForList = isKudosActive
    ? feedbacks
    : feedbacks.filter(feedBackItem => feedBackItem.requestType !== FeedbackRecognitionRequestTypes.recognition);

  const feedbackUuids = orderBy(feedBackDataForList.filter(({ isDraft }) => !isDraft) || [], 'createdAt', ['desc']).map(
    ({ uuid }) => uuid
  );

  const [loaded, setLoaded] = useState(false);
  const [allFeedbacksCount, setAllFeedbacksCount] = useState(0);
  const [currentOffset, setCurrenOffset] = useState(0);

  const hasMoreFeedbacks = !loaded || feedbacks.length < allFeedbacksCount;

  const draftFeedback = useDeepEqualSelector(getDraftFeedbackByWorkspaceUuid(workspaceId));

  useEffect(() => {
    async function fetchFeedback() {
      await dispatch(fetchSingleFeedback(focusedFeedbackUuid));
    }

    if (feedbackUuids.length && focusedFeedbackUuid && !feedbackUuids.includes(focusedFeedbackUuid)) fetchFeedback();
  }, [useDeepCompareMemoize(feedbackUuids)]);

  useEffect(() => {
    async function fetchDrafts() {
      await dispatch(fetchFeedbackDrafts({ workspaceSlug: workspaceId }));
    }

    fetchDrafts();
  }, [workspaceId]);

  const handleCustomizeClick = () => {
    (window as any).analytics.track('Clicked Customize Button', {
      button: 'Customize',
    });
    return history.push('/assistants/manager/settings');
  };

  const handleGiveFeedback = useCallback(
    requestId => {
      (window as any).analytics.track('Clicked Recognition Button', {
        button: 'Give Feedback',
        isGroupWorkspace: isGroup,
      });
      history.push(`/workspaces/${workspaceId}/feedback_request/${requestId}`);
    },
    [workspaceId]
  );

  const handleEditFeedbackClick = useCallback(
    ({ feedbackRecognitionItemUuid: feedbackUuid }) => {
      return history.push(PageUrlsUtil.makeEditFeedbackFormUrl(workspaceId, feedbackUuid));
    },
    [workspaceId]
  );

  const fetchAdditionalFeedbacks = useCallback(async () => {
    const { count } = await dispatch(
      fetchFeedbacks({ workspaceUuid: workspaceId, offset: currentOffset, limit: FETCH_LIMIT })
    );
    setLoaded(true);
    if (allFeedbacksCount !== count) setAllFeedbacksCount(count);
    setCurrenOffset(currentOffset + FETCH_LIMIT);
  }, [dispatch, workspaceId, allFeedbacksCount, currentOffset]);

  const debounceFetchAdditionalFeedbacks = useCallback(debounce(fetchAdditionalFeedbacks, 1000), [
    fetchAdditionalFeedbacks,
  ]);

  const handleDeleteFeedback = useCallback(feedback => dispatch(destroyFeedback(feedback.uuid)), []);

  const handleRequestFeedbackClick = () => {
    if (isNotInvited()) {
      (window as any).analytics.track('Tried to give feedback before inviting user to workspace');
      return toggleIsInviteTeammateModalOpen();
    }
    (window as any).analytics.track('Clicked Feedback/Kudos', {
      button: 'dropdown_carrot',
      feedbackType: 'feedback_request',
    });
    dispatch(FeedbackRequestsActions.createFeedbackRequest({ workspaceId }));
  };

  const feedbackBtnVariant = feedbackUuids.length > 0 ? 'primary' : 'tertiary';

  const getTitle = () => {
    if (isGroup) {
      return 'Kudos';
    }
    if (isKudosActive) {
      return 'Feedback & Kudos';
    }
    return 'Feedback';
  };

  return (
    <>
      <div className="feedbacks-section">
        <div className="">
          <div className="d-flex justify-content-between flex-wrap">
            <h4 className="mb-0">{getTitle()}</h4>
            <WpButtonGroup spacing="md">
              {isManager && !isMobile && (
                <WpButton variant="tertiary" onClick={handleCustomizeClick}>
                  <WpIcon name="cog" />
                  Customize
                </WpButton>
              )}
              {isGroup && feedbackUuids.length > 0 && <GiveKudosButton variant={feedbackBtnVariant} />}
              {!isGroup && (
                <WpButton variant="secondary" onClick={handleRequestFeedbackClick} qaTest="button-request-feedback">
                  Request{!isMobile && <> Feedback</>}
                </WpButton>
              )}
              {!isGroup && <GiveFeedbackAndKudoButton />}
            </WpButtonGroup>
          </div>
        </div>

        {draftFeedback && <DraftFeedback />}
        <div className="feedbacks-section-content">
          <InfiniteScroll
            pageStart={0}
            loadMore={debounceFetchAdditionalFeedbacks}
            hasMore={hasMoreFeedbacks}
            threshold={10}
            loader={<Spinner key="scroll-spinner" activeIf className="mt-5" />}
            useWindow={false}
            getScrollParent={() => pageRef?.current}
          >
            {loaded && allFeedbacksCount === 0 && feedbackUuids.length === 0 && <FeedbackEmptyState />}
            {feedbackUuids.map(feedbackUuid => (
              <FeedbackItem
                key={feedbackUuid}
                isFocused={feedbackUuid === focusedFeedbackUuid}
                feedbackRecognitionItemUuid={feedbackUuid}
                onDeleteFeedback={handleDeleteFeedback}
                onGiveFeedback={handleGiveFeedback}
                onEditFeedbackClick={handleEditFeedbackClick}
                sectionRef={pageRef}
              />
            ))}
          </InfiniteScroll>
        </div>
      </div>
      <InviteTeammateModal
        isOpen={isInviteTeammateModalOpen}
        toggle={toggleIsInviteTeammateModalOpen}
        workspaceId={workspaceId}
        workspaceTitle={workspaceName}
        alertMessage={`Invite ${workspaceName} to PI Perform to request feedback.`}
        afterInviteCallback={toggleIsInviteTeammateModalOpen}
      />
      {showNewFeatureModal && allowIntroModal && <CoachingIntroModal />}
    </>
  );
}

export default FeedbackAndKudosSection;
